"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Modal, ModalHeader, ModalBody, Button, Spinner, Progress, Alert, Input, FormGroup, Label } from "reactstrap"
import { FaPrint, FaExclamationTriangle, FaEye, FaCheckCircle } from "react-icons/fa"
import { toast } from "react-toastify"
import QRCode from "qrcode"
import { listPrinters, silentPrint } from "../../../utils/qzTrayService"
import { bulkBadgeService } from "../../../Service/bulk-badge-print-service"
import { printOrderService } from "../../../Service/print-order-service"
import { renderBadge, prepareBadgeCanvas, BADGE_WIDTH as SHARED_BADGE_WIDTH, BADGE_HEIGHT as SHARED_BADGE_HEIGHT } from "./badgeRenderer"

// Helper function for dynamic image paths
const dynamicImage = (imagePath: string): string => {
  return `/assets/images/${imagePath}`
}

// --- INTERFACES ---
interface BadgeInfo {
  person_full_name: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  stand_category: string
  verification_code: string
  role_color?: string
}

interface BadgeData {
  badge_info: BadgeInfo
  photo_url: string | null
  has_photo: boolean
}

interface BulkBadgeResult {
  representation_id: number
  badge_data: BadgeData
  template_config: any
  verification_code: string
  success: boolean
}

interface BulkBadgeResponse {
  success: boolean
  message: string
  data?: {
    badge_results: BulkBadgeResult[]
    failed_badges: Array<{
      representation_id: number
      error: string
      success: false
    }>
    processing_stats: {
      total_requested: number
      successful: number
      failed: number
      processing_time: number
    }
    print_order_info: {
      id: string
      status: string
      created_at: string
    }
  }
  // Legacy support for direct access (in case backend changes)
  badge_results?: BulkBadgeResult[]
  failed_badges?: Array<{
    representation_id: number
    error: string
    success: false
  }>
  total_processed?: number
  successful_count?: number
  failed_count?: number
}

// --- COMPONENT PROPS ---
interface Props {
  isOpen: boolean
  onToggle: () => void
  staffList: Array<{
    id: number
    person_full_name: string
    person_id: number
    [key: string]: any
  }>
  order: {
    id: number
    order_number: string
    [key: string]: any
  }
  /** Called after the print order is auto-locked (status -> 'completed') following a fully successful bulk print run, so the parent screen can refresh and reflect the new lock state. */
  onOrderCompleted?: () => void
}

const EnhancedBulkBadgePrinter = ({ isOpen, onToggle, staffList, order, onOrderCompleted }: Props) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedBadges, setGeneratedBadges] = useState<string[]>([])
  const [badgeDataList, setBadgeDataList] = useState<BadgeData[]>([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [error, setError] = useState<string | null>(null)
  const [bulkResults, setBulkResults] = useState<BulkBadgeResponse | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null)

  // --- "What-you-print-is-what-you-see" page preview ---
  // One composed image per physical A4 sheet (same composeBadgePage output
  // that actually gets sent to the printer), so the operator can see the
  // real 2x2 layout — margins, gutters, and all — before printing anything.
  const [pagePreviews, setPagePreviews] = useState<string[]>([])
  const [isComposingPages, setIsComposingPages] = useState(false)
  const [previewViewMode, setPreviewViewMode] = useState<"pages" | "individual">("pages")

  // --- Secure (QZ Tray) bulk printing state ---
  const [availablePrinters, setAvailablePrinters] = useState<string[]>([])
  const [selectedPrinter, setSelectedPrinter] = useState<string>("")
  const [printersError, setPrintersError] = useState<string>("")
  const [isPrinting, setIsPrinting] = useState(false)
  const [printProgress, setPrintProgress] = useState({ current: 0, total: 0 })

  // High-quality print settings - optimized for 300 DPI
  // Dimensions sourced from the shared renderer (previously this file used
  // BADGE_HEIGHT=310 while the single-print screen used 311 — a 1px drift
  // that's now impossible since both read the same constant).
  const BADGE_WIDTH = SHARED_BADGE_WIDTH
  const BADGE_HEIGHT = SHARED_BADGE_HEIGHT
  const DPI_SCALE = 8 // Increased for better print quality
  const PRINT_DPI = 300

  // Bulk sheet layout: 4 badges per physical page, 2x2 grid — one A6-sized
  // badge per quadrant of an A4 sheet. Each badge keeps its full design,
  // just tiled instead of getting its own page.
  const BADGES_PER_PAGE = 4
  const GRID_COLS = 2
  const GRID_ROWS = 2
  const A4_PAGE_SIZE_IN = { width: 8.27, height: 11.69 }
  // Badge units are isotropic here (220x310 already matches the A6 ratio,
  // so 1 unit ≈ the same physical distance in both directions). These add
  // white space between badges and around the sheet edge so there's
  // somewhere to put scissors, plus room for the cut-guide lines/marks.
  const GUTTER_UNITS = 12 // gap between adjacent badges (~0.22in)
  const OUTER_MARGIN_UNITS = 8 // gap around the whole sheet (~0.15in)

  // ---------------------------------------------------------------------------
  // Anti-waste bulk-print split
  //
  // Only print completely filled 2×2 pages. Any remainder that cannot fill a
  // full page is held back and printed individually via Single Badge (A6).
  //
  //   completePages    = floor(total / 4)
  //   bulkBadgeCount   = completePages × 4
  //   remainingCount   = total % 4
  //
  // The API call is made with only the first `bulkBadgeCount` entries from
  // staffList. The trailing `remainingCount` entries naturally stay pending
  // in the staff list for single-badge printing.
  // ---------------------------------------------------------------------------
  const totalBadges = staffList.length
  const completePages = Math.floor(totalBadges / BADGES_PER_PAGE)
  const bulkBadgeCount = completePages * BADGES_PER_PAGE
  const remainingCount = totalBadges % BADGES_PER_PAGE

  /** The slice of staffList that will be sent to the bulk-generate API */
  const bulkStaffList = staffList.slice(0, bulkBadgeCount)

  // Reset state on modal close
  useEffect(() => {
    if (!isOpen) {
      setIsGenerating(false)
      setGeneratedBadges([])
      setBadgeDataList([])
      setProgress({ current: 0, total: 0 })
      setError(null)
      setBulkResults(null)
      setShowPreview(false)
      setIsPrinting(false)
      setPrintProgress({ current: 0, total: 0 })
      setPagePreviews([])
      setIsComposingPages(false)
      setPreviewViewMode("pages")
    }
  }, [isOpen])

  // Discover QZ Tray printers as soon as the modal opens, so the operator
  // can pick one before printing. Failure is non-fatal here — it just means
  // QZ Tray isn't reachable yet; the error is surfaced when printing starts.
  useEffect(() => {
    if (!isOpen) {
      setAvailablePrinters([])
      setPrintersError("")
      return
    }
    let cancelled = false
    listPrinters()
      .then((printers) => {
        if (cancelled) return
        const names = printers.map((p) => p.name)
        setAvailablePrinters(names)
        setPrintersError("")
        setSelectedPrinter((current) => current || names[0] || "")
      })
      .catch((err) => {
        if (cancelled) return
        setPrintersError(err?.message || "QZ Tray is not reachable. Install/start QZ Tray to print.")
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const handleGeneratePreview = async () => {
    setIsGenerating(true)
    setError(null)
    setGeneratedBadges([])
    setBadgeDataList([])
    setProgress({ current: 0, total: bulkBadgeCount })

    try {
      console.log(
        `[v0] Starting bulk badge preview generation — ${bulkBadgeCount} of ${totalBadges} badges ` +
        `(${completePages} full page(s), ${remainingCount} held back for single print)`
      )

      // Only send the first `bulkBadgeCount` representations to the API.
      // The trailing `remainingCount` items are intentionally excluded so
      // they remain pending for individual A6 printing.
      const bulkData = (await bulkBadgeService.generateBulkBadges({
        print_order_id: order.id,
        staff_representations: bulkStaffList.map((staff) => staff.id),
      })) as BulkBadgeResponse
      console.log("[v0] Bulk API Response:", bulkData)

      setBulkResults(bulkData)

      if (!bulkData.success) {
        throw new Error(bulkData.message)
      }

      const badgeResults = bulkData.data?.badge_results || bulkData.badge_results || []
      const failedBadges = bulkData.data?.failed_badges || bulkData.failed_badges || []

      console.log("[v0] Badge results found:", badgeResults.length)
      console.log("[v0] Failed badges found:", failedBadges.length)

      if (failedBadges.length > 0) {
        toast.warning(`${failedBadges.length} badges failed to generate. Check the details below.`)
      }

      if (!Array.isArray(badgeResults)) {
        throw new Error("Invalid response format: badge_results is not an array")
      }

      const badgeDataArray = badgeResults.map((result) => result.badge_data)
      setBadgeDataList(badgeDataArray)

      console.log(`[v0] Starting canvas generation for ${badgeDataArray.length} badges`)

      const generatedBadgeImages: string[] = []

      for (let i = 0; i < badgeDataArray.length; i++) {
        try {
          console.log(`[v0] Generating badge ${i + 1}/${badgeDataArray.length}`)
          setProgress({ current: i, total: badgeDataArray.length })

          const badgeImage = await generateSingleBadge(badgeDataArray[i])
          generatedBadgeImages.push(badgeImage)

          console.log(`[v0] Badge ${i + 1} generated successfully`)
        } catch (err) {
          console.error(`[v0] Failed to generate badge ${i + 1}:`, err)
          generatedBadgeImages.push("") // Empty string for failed badges
        }
      }

      setGeneratedBadges(generatedBadgeImages)
      setProgress({ current: badgeDataArray.length, total: badgeDataArray.length })

      const successCount = generatedBadgeImages.filter((badge) => badge).length
      const remainingMsg = remainingCount > 0
        ? ` · ${remainingCount} badge(s) held back for single (A6) print.`
        : ""
      toast.success(`Generated ${successCount} of ${badgeDataArray.length} bulk badges successfully!${remainingMsg}`)

      setIsGenerating(false)
      setShowPreview(true)

      // Compose the actual print-ready sheets (same function used at print
      // time) so the preview shown to the operator is pixel-for-pixel what
      // will come out of the printer — 2x2, with real margins/gutters.
      setIsComposingPages(true)
      try {
        const composedPages: string[] = []
        for (let i = 0; i < generatedBadgeImages.length; i += BADGES_PER_PAGE) {
          const pageSlice = generatedBadgeImages.slice(i, i + BADGES_PER_PAGE)
          try {
            const pageImage = await composeBadgePage(pageSlice)
            composedPages.push(pageImage)
          } catch (pageErr) {
            console.error(`[v0] Failed to compose page preview ${composedPages.length + 1}:`, pageErr)
            composedPages.push("")
          }
        }
        setPagePreviews(composedPages)
      } finally {
        setIsComposingPages(false)
      }
    } catch (err: any) {
      console.error("[v0] Bulk badge generation failed:", err)
      const errorMessage = err.message || "Unknown error occurred"
      setError(errorMessage)
      toast.error(`Preview generation failed: ${errorMessage}`)
      setIsGenerating(false)
    }
  }

  // Strip the "data:image/png;base64," prefix — QZ Tray wants raw base64.
  const dataUrlToBase64 = (dataUrl: string): string => dataUrl.substring(dataUrl.indexOf(",") + 1)

  /**
   * Sends generated badges to the selected printer via QZ Tray, batched
   * four-to-a-page in a 2x2 grid (composeBadgePage does the tiling) — no
   * browser print dialog, no PDF/PNG download. Badges never leave memory
   * except to travel to the printer itself. One QZ print job = one physical
   * sheet with up to 4 badges, instead of one job per badge.
   */
  const handleSecureBulkPrint = async () => {
    // Pair each image with its badge data by original index so failed
    // generations (empty string) don't shift later badges out of alignment.
    const badgesToPrint = generatedBadges
      .map((image, idx) => ({ image, data: badgeDataList[idx] }))
      .filter((b): b is { image: string; data: BadgeData } => !!b.image)

    if (badgesToPrint.length === 0) {
      toast.error("No badges available to print")
      return
    }
    if (!selectedPrinter) {
      toast.error("Select a printer before printing.")
      return
    }

    const pages: { image: string; data: BadgeData }[][] = []
    for (let i = 0; i < badgesToPrint.length; i += BADGES_PER_PAGE) {
      pages.push(badgesToPrint.slice(i, i + BADGES_PER_PAGE))
    }

    setIsPrinting(true)
    setPrintProgress({ current: 0, total: pages.length })

    let successPages = 0
    let successBadges = 0
    for (let p = 0; p < pages.length; p++) {
      const pageBadges = pages[p]
      try {
        const pageImage = await composeBadgePage(pageBadges.map((b) => b.image))
        await silentPrint({
          printerName: selectedPrinter,
          base64Data: dataUrlToBase64(pageImage),
          format: "image",
          size: A4_PAGE_SIZE_IN,
        })
        successPages++
        successBadges += pageBadges.length
      } catch (err: any) {
        const names = pageBadges
          .map((b) => b.data?.badge_info.person_full_name)
          .filter(Boolean)
          .join(", ")
        console.error(`Failed to print page ${p + 1}${names ? ` (${names})` : ""}:`, err)
        toast.error(`Failed to print page ${p + 1}${names ? ` (${names})` : ""}: ${err?.message || "Unknown error"}`)
      }
      setPrintProgress({ current: p + 1, total: pages.length })
    }

    setIsPrinting(false)
    if (successPages === pages.length) {
      toast.success(
        `All ${pages.length} page${pages.length > 1 ? "s" : ""} (${successBadges} badges) sent to ${selectedPrinter} successfully!`,
      )

      // Only auto-lock the order when every badge that was generated also
      // printed successfully AND there are no remaining badges held back for
      // single-badge (A6) printing.
      //
      // If remainingCount > 0, the order must stay open so the operator can
      // print the remaining badges individually from the staff list.
      const allGeneratedBadgesPrinted = badgesToPrint.length === generatedBadges.length

      if (allGeneratedBadgesPrinted && remainingCount === 0) {
        // ---- All badges fit evenly into full pages: lock the order ----
        try {
          await printOrderService.completePrintOrder(order.id)
          toast.info("All badges printed. Print order marked as completed.")
          onOrderCompleted?.()
          // Auto-close this modal, then reload so the order moves to the
          // Completed tab in the list behind it.
          onToggle()
          setTimeout(() => {
            window.location.reload()
          }, 1200)
        } catch (err: any) {
          console.error("Failed to mark print order as completed:", err)
          toast.warning(
            `Badges printed, but failed to lock the print order: ${err?.response?.data?.message || err?.message || "Unknown error"}`,
          )
        }
      } else if (allGeneratedBadgesPrinted && remainingCount > 0) {
        // ---- Some badges remain for single (A6) printing ----
        toast.info(
          `${remainingCount} badge${remainingCount !== 1 ? "s" : ""} held back — ` +
          `print ${remainingCount !== 1 ? "them" : "it"} individually using Single Badge (A6) printing from the staff list.`,
        )
        // Notify parent to refresh the staff list so the pending badges
        // are clearly visible, but do NOT lock the order yet.
        onOrderCompleted?.()
        onToggle()
      }
      // else: some pages failed mid-run — warn already shown per failed page;
      // operator can retry without the order being locked.
    } else {
      toast.warning(`${successPages} of ${pages.length} pages printed successfully (${successBadges} badges).`)
    }
  }

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous"

      const timeout = setTimeout(() => {
        reject(new Error(`Image load timeout: ${src}`))
      }, 10000) // 10 second timeout

      img.onload = () => {
        clearTimeout(timeout)
        resolve(img)
      }

      img.onerror = () => {
        clearTimeout(timeout)
        reject(new Error(`Failed to load image: ${src}`))
      }

      img.src = src
    })
  }

  /**
   * Tiles up to 4 already-rendered badge images (data URLs from
   * generateSingleBadge) into a single 2x2 sheet, at the same DPI_SCALE
   * used per-badge so nothing gets soft. Each badge is drawn into its
   * quadrant unmodified — same design, same proportions — but now with a
   * white gutter between badges and around the sheet edge (room for
   * scissors). Empty quadrants (last page with fewer than 4 badges) are
   * simply left blank/white.
   */
  const composeBadgePage = async (images: string[]): Promise<string> => {
    const cellW = BADGE_WIDTH * DPI_SCALE
    const cellH = BADGE_HEIGHT * DPI_SCALE
    const gutter = GUTTER_UNITS * DPI_SCALE
    const margin = OUTER_MARGIN_UNITS * DPI_SCALE

    const pageCanvas = document.createElement("canvas")
    pageCanvas.width = cellW * GRID_COLS + gutter * (GRID_COLS - 1) + margin * 2
    pageCanvas.height = cellH * GRID_ROWS + gutter * (GRID_ROWS - 1) + margin * 2

    const ctx = pageCanvas.getContext("2d")
    if (!ctx) {
      throw new Error("Could not get page canvas context")
    }

    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)

    for (let i = 0; i < Math.min(images.length, BADGES_PER_PAGE); i++) {
      const src = images[i]
      if (!src) continue
      const img = await loadImage(src)
      const col = i % GRID_COLS
      const row = Math.floor(i / GRID_COLS)
      const x = margin + col * (cellW + gutter)
      const y = margin + row * (cellH + gutter)
      ctx.drawImage(img, x, y, cellW, cellH)
    }

    drawCutGuides(ctx, pageCanvas.width, pageCanvas.height, cellW, cellH, gutter, margin)

    return pageCanvas.toDataURL("image/png", 1.0)
  }

  /**
   * Faint dash-dot cutting guides down the middle of each gutter, so the
   * operator has a line to follow with scissors — thin enough to stay out
   * of the way and not eat into either badge's design. A tiny scissors
   * glyph sits at the crossing point of the vertical/horizontal guides as
   * a quick visual cue for where to start cutting.
   */
  const drawCutGuides = (
    ctx: CanvasRenderingContext2D,
    pageWidth: number,
    pageHeight: number,
    cellW: number,
    cellH: number,
    gutter: number,
    margin: number,
  ) => {
    const lineWidth = Math.max(1, DPI_SCALE * 0.25)
    const dash = DPI_SCALE * 1.2
    const gap = DPI_SCALE * 1
    const dot = Math.max(1, DPI_SCALE * 0.25)

    ctx.save()
    ctx.strokeStyle = "#b3b3b3"
    ctx.lineWidth = lineWidth
    ctx.lineCap = "round"
    ctx.setLineDash([dash, gap, dot, gap])

    // Vertical guide — middle of the gutter between the two columns,
    // running the full height of the sheet (through badges + margins).
    if (GRID_COLS > 1) {
      const vx = margin + cellW + gutter / 2
      ctx.beginPath()
      ctx.moveTo(vx, 0)
      ctx.lineTo(vx, pageHeight)
      ctx.stroke()
    }

    // Horizontal guide — middle of the gutter between the two rows.
    if (GRID_ROWS > 1) {
      const hy = margin + cellH + gutter / 2
      ctx.beginPath()
      ctx.moveTo(0, hy)
      ctx.lineTo(pageWidth, hy)
      ctx.stroke()
    }

    ctx.setLineDash([])

    // Small scissors mark at the crossing point, sized to sit inside the
    // gutter without touching either badge.
    if (GRID_COLS > 1 && GRID_ROWS > 1) {
      const cx = margin + cellW + gutter / 2
      const cy = margin + cellH + gutter / 2
      const fontSize = Math.max(8, Math.min(gutter, DPI_SCALE * 3.5))
      ctx.font = `${fontSize}px sans-serif`
      ctx.fillStyle = "#999999"
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText("✂", cx, cy)
    }

    ctx.restore()
  }

  const generateSingleBadge = useCallback(async (badgeData: BadgeData): Promise<string> => {
    console.log("[v0] Starting badge generation for:", badgeData.badge_info.person_full_name)

    const canvas = hiddenCanvasRef.current
    if (!canvas) {
      console.error("[v0] Canvas element not found")
      throw new Error("Canvas element not found")
    }

    try {
      console.log("[v0] Loading images for badge...")

      const qrCodePromise = QRCode.toDataURL(badgeData.badge_info.verification_code, {
        width: 120 * DPI_SCALE,
        margin: 2,
        errorCorrectionLevel: "H",
        type: "image/png",
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      }).then((qrUrl) => {
        console.log("[v0] QR code generated, length:", qrUrl.length)
        return loadImage(qrUrl)
      })

      const photoPromise =
        badgeData.has_photo && badgeData.photo_url
          ? loadImage(badgeData.photo_url).catch((err) => {
              console.log("[v0] Photo load failed, using fallback:", err.message)
              return null
            })
          : Promise.resolve(null)

      const logoPromise = loadImage(dynamicImage("logo.PNG")).catch((err) => {
        console.log("[v0] Logo load failed, using fallback:", err.message)
        return null
      })

      const [qrImageElement, photoImage, psfLogoImage] = await Promise.all([qrCodePromise, photoPromise, logoPromise])
      console.log("[v0] All images loaded successfully")

      // Shared setup (dimensions, DPI scale, smoothing) — identical to single print.
      const preparedCtx = prepareBadgeCanvas(canvas, DPI_SCALE)

      console.log("[v0] Canvas configured, starting drawing...")

      // Single call into the shared renderer — this is the ONLY badge
      // drawing implementation in the whole app now, so bulk print and
      // single print are guaranteed to produce pixel-identical layouts,
      // regardless of name/role/company text length.
      renderBadge(
        preparedCtx,
        {
          badge_info: {
            person_full_name: badgeData.badge_info.person_full_name,
            role: badgeData.badge_info.role,
            exhibitor_name: badgeData.badge_info.exhibitor_name,
            exhibition_title: badgeData.badge_info.exhibition_title,
            stand_category: badgeData.badge_info.stand_category,
            role_color: badgeData.badge_info.role_color,
          },
          has_photo: badgeData.has_photo,
        },
        {
          photoImage,
          qrImage: qrImageElement,
          logoImage: psfLogoImage,
          showLogo: false,
        },
      )

      console.log("[v0] Badge drawing completed")

      const dataUrl = canvas.toDataURL("image/png", 1.0)
      console.log("[v0] Badge data URL generated, length:", dataUrl.length)

      return dataUrl
    } catch (error) {
      console.error("[v0] Error generating badge:", error)
      throw error
    }
  }, [])

  const renderContent = () => {
    if (error) {
      return (
        <Alert color="danger">
          <h5 className="alert-heading">
            <FaExclamationTriangle /> Preview Generation Failed
          </h5>
          <p>The preview generation process encountered an error:</p>
          <hr />
          <p className="mb-0">
            <code>{error}</code>
          </p>
          {(bulkResults?.data?.failed_badges || bulkResults?.failed_badges) &&
            ((bulkResults?.data?.failed_badges?.length || 0) > 0 || (bulkResults?.failed_badges?.length || 0) > 0) && (
              <div className="mt-3">
                <h6>Failed Badges:</h6>
                <ul className="mb-0">
                  {(bulkResults.data?.failed_badges || bulkResults.failed_badges || []).map((failed, index) => (
                    <li key={index}>
                      Representation ID {failed.representation_id}: {failed.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </Alert>
      )
    }

    if (isGenerating) {
      const percentage = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0
      return (
        <div className="text-center p-4">
          <h4>Generating Badge Previews...</h4>
          <p className="text-muted">
            Processing {progress.current} of {progress.total} badges...
          </p>
          <Progress
            animated={isGenerating}
            color="success"
            value={percentage}
            className="my-3"
            style={{ height: "20px" }}
          >
            {percentage}%
          </Progress>
          <Spinner color="primary" />
        </div>
      )
    }

    if (showPreview && generatedBadges.length > 0) {
      const completedBadges = generatedBadges.filter((badge) => badge)

      return (
        <div className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
            <div>
              <h4 className="mb-1">
                <FaEye className="me-2" />
                Badge Preview
              </h4>
              <p className="text-muted mb-0">
                {completedBadges.length} of {generatedBadges.length} bulk badges ready • 2×2 grid, 4 per page (
                {Math.ceil(completedBadges.length / BADGES_PER_PAGE)} page
                {Math.ceil(completedBadges.length / BADGES_PER_PAGE) === 1 ? "" : "s"})
                {remainingCount > 0 && (
                  <span className="ms-2 text-warning">
                    · {remainingCount} badge{remainingCount !== 1 ? "s" : ""} pending single (A6) print
                  </span>
                )}
              </p>
            </div>

            {/* Toggle between the real print-sheet preview and per-badge cards */}
            <div className="btn-group" role="group" aria-label="Preview view mode">
              <Button
                size="sm"
                color={previewViewMode === "pages" ? "primary" : "outline-secondary"}
                onClick={() => setPreviewViewMode("pages")}
              >
                Print Layout (2×2)
              </Button>
              <Button
                size="sm"
                color={previewViewMode === "individual" ? "primary" : "outline-secondary"}
                onClick={() => setPreviewViewMode("individual")}
              >
                Individual Badges
              </Button>
            </div>
          </div>

          {/* QZ Tray secure printing controls */}
          <Alert color="light" className="border mb-4">
            <div className="d-flex flex-wrap align-items-end gap-3">
              <FormGroup className="mb-0" style={{ minWidth: "260px" }}>
                <Label className="small fw-bold mb-1">
                  <FaPrint className="me-1" />
                  Printer
                </Label>
                <Input
                  type="select"
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  disabled={isPrinting || availablePrinters.length === 0}
                >
                  {availablePrinters.length === 0 ? (
                    <option value="">No printers found</option>
                  ) : (
                    availablePrinters.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))
                  )}
                </Input>
                {printersError && <div className="text-danger small mt-1">{printersError}</div>}
              </FormGroup>

              <Button
                color="primary"
                onClick={handleSecureBulkPrint}
                disabled={completedBadges.length === 0 || isPrinting || !selectedPrinter}
              >
                {isPrinting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Printing page {printProgress.current}/{printProgress.total}...
                  </>
                ) : (
                  <>
                    <FaPrint className="me-2" />
                    Print {completedBadges.length} Bulk Badge{completedBadges.length !== 1 ? "s" : ""} via QZ Tray
                    {" "}({Math.ceil(completedBadges.length / BADGES_PER_PAGE)} full page
                    {Math.ceil(completedBadges.length / BADGES_PER_PAGE) === 1 ? "" : "s"})
                    {remainingCount > 0 && ` · ${remainingCount} for single print`}
                  </>
                )}
              </Button>

              {printProgress.current > 0 && printProgress.current === printProgress.total && !isPrinting && (
                <span className="text-success small">
                  <FaCheckCircle className="me-1" />
                  Done
                </span>
              )}
            </div>
            {isPrinting && (
              <Progress
                animated
                color="success"
                value={Math.round((printProgress.current / Math.max(printProgress.total, 1)) * 100)}
                className="mt-3"
                style={{ height: "10px" }}
              />
            )}
          </Alert>

          {previewViewMode === "pages" ? (
            <div className="mb-4">
              {isComposingPages ? (
                <div className="text-center py-5">
                  <Spinner color="primary" className="mb-3" />
                  <p className="text-muted mb-0">Composing print sheets…</p>
                </div>
              ) : pagePreviews.length === 0 ? (
                <Alert color="warning" className="mb-0">
                  <FaExclamationTriangle className="me-2" />
                  Could not compose page previews. Try the "Individual Badges" view instead.
                </Alert>
              ) : (
                <>
                  <p className="text-muted small mb-3">
                    Each sheet below is exactly what will come out of the printer — an A4 page with up to{" "}
                    {BADGES_PER_PAGE} badges arranged {GRID_COLS}×{GRID_ROWS}, with the same margins and cutting
                    gutters used for the real print job.
                  </p>
                  <div className="d-flex flex-wrap gap-4">
                    {pagePreviews.map((pageImage, pageIndex) => {
                      const pageBadgeData = badgeDataList.slice(
                        pageIndex * BADGES_PER_PAGE,
                        pageIndex * BADGES_PER_PAGE + BADGES_PER_PAGE,
                      )
                      return (
                        <div key={pageIndex} style={{ width: "300px" }}>
                          <div
                            className="d-flex justify-content-between align-items-center mb-2"
                          >
                            <strong className="small">
                              Page {pageIndex + 1} of {pagePreviews.length}
                            </strong>
                            <span className="badge bg-secondary">A4 · {GRID_COLS}×{GRID_ROWS}</span>
                          </div>
                          <div
                            className="bg-white border rounded shadow-sm mx-auto"
                            style={{
                              width: "100%",
                              aspectRatio: `${A4_PAGE_SIZE_IN.width} / ${A4_PAGE_SIZE_IN.height}`,
                              overflow: "hidden",
                            }}
                          >
                            {pageImage ? (
                              <img
                                src={pageImage || "/placeholder.svg"}
                                alt={`Print sheet ${pageIndex + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center h-100 text-danger">
                                <FaExclamationTriangle className="me-2" />
                                Failed to compose
                              </div>
                            )}
                          </div>
                          <ul className="list-unstyled small text-muted mt-2 mb-0">
                            {pageBadgeData.map((data, i) => (
                              <li key={i} className="text-truncate">
                                {i + 1}. {data?.badge_info.person_full_name || "—"}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="row g-3 mb-4">
              {generatedBadges.map((badgeImage, index) => (
                <div key={index} className="col-md-3 col-sm-6">
                  <div className="card h-100">
                    <div className="card-body text-center p-3">
                      {badgeImage ? (
                        <img
                          src={badgeImage || "/placeholder.svg"}
                          alt={`Badge ${index + 1}`}
                          className="img-fluid rounded shadow-sm"
                          style={{ maxHeight: "300px", objectFit: "contain" }}
                        />
                      ) : (
                        <div className="bg-light rounded p-4 text-center">
                          <div className="text-danger">
                            <FaExclamationTriangle className="mb-2" />
                            <p className="text-muted mb-0">Generation Failed</p>
                          </div>
                        </div>
                      )}
                      <div className="mt-2">
                        <small className="text-muted">
                          {badgeDataList[index] && (
                            <>
                              <strong>{badgeDataList[index].badge_info.person_full_name}</strong>
                              <br />
                              {badgeDataList[index].badge_info.role}
                            </>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      )
    }

    // ---- Edge case: fewer than 4 badges total — nothing for bulk printing ----
    if (bulkBadgeCount === 0) {
      return (
        <div className="p-4">
          <Alert color="warning">
            <h5 className="alert-heading">
              <FaExclamationTriangle className="me-2" />
              Not Enough Badges for Bulk Printing
            </h5>
            <p className="mb-1">
              Bulk printing fills <strong>complete 2×2 pages</strong> (4 badges per sheet).
              This order has only <strong>{totalBadges}</strong> badge{totalBadges !== 1 ? "s" : ""}, which cannot fill
              a single full page.
            </p>
            <hr />
            <p className="mb-0">
              Please use <strong>Single Badge (A6)</strong> printing for all {totalBadges}{" "}
              badge{totalBadges !== 1 ? "s" : ""} from the staff list.
            </p>
          </Alert>
        </div>
      )
    }

    // ---- Normal state: show split summary and Generate button ----
    return (
      <div className="text-center p-4">
        <h4>Preview-First Bulk Badge Printing</h4>
        <p className="text-muted">
          Preview and print <strong>{bulkBadgeCount}</strong> of{" "}
          <strong>{totalBadges}</strong> badges in bulk.
        </p>

        {/* Anti-waste split summary — only shown when there's a remainder */}
        {remainingCount > 0 ? (
          <Alert
            color="success"
            className="text-start mb-4 text-white"
            style={{ backgroundColor: "#198754", borderColor: "#198754" }}
          >
            <h6 className="alert-heading mb-2 text-white">Print Split Summary</h6>
            <table className="table table-sm table-borderless mb-0 text-white">
              <tbody>
                <tr>
                  <td className="fw-semibold">Total Badges:</td>
                  <td><strong>{totalBadges}</strong></td>
                </tr>
                <tr>
                  <td className="fw-semibold">Printed in Bulk:</td>
                  <td>
                    <strong>{bulkBadgeCount}</strong>
                    <span className="text-white-50 ms-2 small">
                      ({completePages} full page{completePages !== 1 ? "s" : ""}, 4 badges each)
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="fw-semibold">Pending Single Print:</td>
                  <td>
                    <strong>{remainingCount}</strong>
                    <span className="text-white-50 ms-2 small">
                      (print individually via Single Badge A6 after this)
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <hr className="my-2" style={{ borderColor: "rgba(255,255,255,0.35)" }} />
            <p className="mb-0 small text-white-50">
              To avoid paper waste, only completely filled 2×2 pages are bulk-printed.
              The remaining <strong className="text-white">{remainingCount}</strong>{" "}
              badge{remainingCount !== 1 ? "s" : ""} will stay pending and appear
              in the staff list for individual A6 printing.
            </p>
          </Alert>
        ) : (
          <Alert color="success" className="text-start mb-4">
            <FaCheckCircle className="me-2" />
            All <strong>{totalBadges}</strong> badges fill exactly{" "}
            <strong>{completePages}</strong> complete page{completePages !== 1 ? "s" : ""} — no paper waste!
          </Alert>
        )}

        <Button color="primary" size="lg" onClick={handleGeneratePreview} className="mt-2">
          <FaEye className="me-2" />
          Generate Badge Previews ({bulkBadgeCount} badges)
        </Button>
      </div>
    )
  }

  return (
    <>
      <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />

      <Modal isOpen={isOpen} toggle={onToggle} size="xl" centered>
        <ModalHeader toggle={onToggle}>Preview-First Bulk Badge Printing - Order #{order.order_number}</ModalHeader>
        <ModalBody>{renderContent()}</ModalBody>
      </Modal>
    </>
  )
}

export default EnhancedBulkBadgePrinter